import { useState } from "react";
import { Reply, Pencil, Trash2, Paperclip } from "lucide-react";
import NoteComposer from "./NoteComposer";

// Format an ISO timestamp as "just now / 5m / 2h / 3d / MMM d"
function relTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, now - then) / 1000;
  if (diff < 45) return "just now";
  if (diff < 3600) return `${Math.round(diff / 60)}m`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h`;
  if (diff < 604800) return `${Math.round(diff / 86400)}d`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function initials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  const first = parts[0] && parts[0][0];
  const second = parts[1] && parts[1][0];
  return ((first || "") + (second || "")).toUpperCase() || "?";
}

/**
 * Renders a single note (no recursion — the parent flattens the tree via DFS
 * and hands each node a `depth` value). Splitting recursion out of the JSX
 * sidesteps a Babel/CRA stack-overflow bug that fires on self-referential
 * JSX children.
 */
export default function NoteNode({
  note, depth, currentUserId, noticeId,
  replyOpen, setReplyOpen, editOpen, setEditOpen,
  onReply, onEdit, onDelete, posting,
}) {
  const isOwner = note.author_user_id === currentUserId;
  const isReplying = replyOpen === note.note_id;
  const isEditing = editOpen === note.note_id;
  const [editBody, setEditBody] = useState(note.body);
  const marginLeft = Math.min(depth, 6) * 24;

  const attachmentLinks = (note.attachments || []).map((a) => {
    const url = process.env.REACT_APP_BACKEND_URL + "/api/opportunities/" + noticeId + "/notes/" + note.note_id + "/attachments/" + a.attachment_id;
    return (
      <a
        key={a.attachment_id}
        href={url}
        target="_blank"
        rel="noreferrer"
        className="border border-white/10 hover:border-cyan-400/60 px-3 py-1.5 text-xs text-slate-300 hover:text-cyan-400 inline-flex items-center gap-2"
        data-testid={"note-attachment-" + a.attachment_id}
      >
        <Paperclip className="w-3.5 h-3.5" />
        <span className="truncate max-w-[220px]">{a.filename}</span>
        <span className="text-slate-500">· {Math.round((a.size || 0) / 1024)} KB</span>
      </a>
    );
  });

  return (
    <div style={{ marginLeft }} data-testid={"note-" + note.note_id}>
      <div className="bg-[#0D1424] border border-white/10 p-4">
        <div className="flex items-center gap-3 mb-2">
          <div
            className="w-7 h-7 shrink-0 flex items-center justify-center bg-cyan-400/10 text-cyan-400 font-display font-bold text-xs border border-cyan-400/30"
            aria-hidden
          >
            {initials(note.author_name)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm text-white font-medium truncate">{note.author_name || note.author_email || "Team member"}</div>
            <div className="mono-label text-slate-500 text-[10px]">
              {relTime(note.created_at)}{note.edited && <span className="ml-2 text-slate-600">· edited</span>}
            </div>
          </div>
        </div>

        {isEditing ? (
          <div>
            <textarea
              value={editBody}
              onChange={(e) => setEditBody(e.target.value)}
              className="w-full bg-black/40 border border-white/10 focus:border-cyan-400/60 outline-none text-sm text-slate-200 p-3 min-h-[70px]"
              data-testid={"note-edit-textarea-" + note.note_id}
              autoFocus
            />
            <div className="flex gap-2 mt-2">
              <button
                onClick={() => onEdit(note.note_id, editBody.trim())}
                disabled={!editBody.trim()}
                className="bf-pill px-4 py-1.5 text-xs tracking-widest uppercase font-display font-bold inline-flex items-center gap-2 disabled:opacity-50"
                data-testid={"note-edit-save-" + note.note_id}
              >SAVE</button>
              <button
                onClick={() => { setEditOpen(null); setEditBody(note.body); }}
                className="border border-white/10 hover:border-white/30 px-4 py-1.5 text-xs tracking-widest uppercase font-display font-bold"
                data-testid={"note-edit-cancel-" + note.note_id}
              >CANCEL</button>
            </div>
          </div>
        ) : (
          <div className="text-sm text-slate-300 whitespace-pre-wrap leading-relaxed" data-testid={"note-body-" + note.note_id}>
            {note.body}
          </div>
        )}

        {attachmentLinks.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">{attachmentLinks}</div>
        )}

        {!isEditing && (
          <div className="mt-3 flex items-center gap-3 text-xs mono-label">
            <button
              onClick={() => setReplyOpen(isReplying ? null : note.note_id)}
              className="text-slate-500 hover:text-cyan-400 inline-flex items-center gap-1"
              data-testid={"note-reply-btn-" + note.note_id}
            >
              <Reply className="w-3.5 h-3.5" />REPLY
            </button>
            {isOwner && (
              <>
                <button
                  onClick={() => { setEditBody(note.body); setEditOpen(note.note_id); }}
                  className="text-slate-500 hover:text-cyan-400 inline-flex items-center gap-1"
                  data-testid={"note-edit-btn-" + note.note_id}
                >
                  <Pencil className="w-3.5 h-3.5" />EDIT
                </button>
                <button
                  onClick={() => onDelete(note.note_id)}
                  className="text-slate-500 hover:text-red-400 inline-flex items-center gap-1"
                  data-testid={"note-delete-btn-" + note.note_id}
                >
                  <Trash2 className="w-3.5 h-3.5" />DELETE
                </button>
              </>
            )}
          </div>
        )}

        {isReplying && (
          <div className="mt-3 border-t border-white/5 pt-3">
            <NoteComposer
              placeholder={"Reply to " + (note.author_name || "this note") + "…"}
              onSubmit={(body, files) => onReply(note.note_id, body, files)}
              onCancel={() => setReplyOpen(null)}
              disabled={posting}
              autoFocus
              testIdPrefix={"reply-" + note.note_id}
            />
          </div>
        )}
      </div>
    </div>
  );
}
